import React from 'react';
import { EpicSummary, GROUP_ORDER, GROUP_STYLE, StatusGroup } from './types';
import * as fmt from '../../lib/format';

interface Props {
  counts: EpicSummary['counts']['tickets'];
  filter: StatusGroup | null;
  onFilter: (group: StatusGroup | null) => void;
}

// Where the epic stands: one number, one bar, and the status mix as chips that double as
// a filter for the ticket list below.
export default function ProgressBlock({ counts, filter, onFilter }: Props) {
  const { counted, by_group, progress_pct } = counts;
  const barGroups: StatusGroup[] = ['done', 'in_progress', 'on_hold', 'todo'];
  const present = GROUP_ORDER.filter((g) => by_group[g] > 0);

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400" aria-label="Progress">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">Progress</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {counted === 0 ? 'Nothing to measure yet' : `${by_group.done} of ${counted} ${counted === 1 ? 'ticket' : 'tickets'} done`}
          </p>
        </div>
        <div className="text-3xl font-semibold tabular-nums tracking-tight text-gray-900">
          {fmt.percent(progress_pct)}
        </div>
      </div>

      <div
        role="progressbar"
        aria-label="Share of tickets done"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress_pct ?? 0}
        className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-gray-100"
      >
        {counted > 0 &&
          barGroups.map((g) =>
            by_group[g] > 0 ? (
              <div
                key={g}
                className={`${GROUP_STYLE[g].bar} transition-[width] duration-500 ease-out motion-reduce:transition-none`}
                style={{ width: `${(by_group[g] / counted) * 100}%`, minWidth: 3 }}
              />
            ) : null
          )}
      </div>

      {present.length > 0 && (
        <>
          <div className="mt-3.5 flex flex-wrap gap-2">
            {present.map((g) => {
              const on = filter === g;
              const s = GROUP_STYLE[g];
              return (
                <button
                  key={g}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onFilter(on ? null : g)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition-[transform,background-color,box-shadow] duration-150 ease-out motion-reduce:transition-none active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${on ? s.chipOn : s.chip}`}
                >
                  <span className={`h-2 w-2 rounded-full ${s.dot}`} aria-hidden />
                  {s.label}
                  <span className="font-semibold tabular-nums">{by_group[g]}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2.5 text-xs text-gray-500">Select a status to see just those tickets.</p>
        </>
      )}
    </section>
  );
}
